<template>
  <div v-if="widgetDataValue.loading" class="bg-white rounded pa-6">
    <v-skeleton-loader type="subtitle" width="40%" class="mb-4 bg-transparent"></v-skeleton-loader>
    <v-skeleton-loader type="image" height="160" class="bg-transparent"></v-skeleton-loader>
    <v-skeleton-loader type="paragraph@2" class="bg-transparent"></v-skeleton-loader>
  </div>

  <div v-else-if="widgetDataValue.data" class="cloud-backup-widget">
    <!-- Small widget: summary number cards -->
    <div v-if="showSummaryWidgets" class="cloud-backup-widget__summary mb-4">
      <v-card
        v-for="direction in directions"
        :key="`summary-${direction.id}`"
        class="cloud-backup-widget__summary-card pa-6"
        :data-testid="`cloud-backup-${direction.id}-summary`"
      >
        <div class="big-number text-primary">{{ direction.total }}</div>
        <div class="body2 mt-1">{{ direction.summaryLabel }}</div>
        <div
          v-if="direction.lastRunLabel"
          class="caption text-indigo-medium"
          :data-testid="`cloud-backup-${direction.id}-last-run`"
        >
          {{ direction.lastRunLabel }}
        </div>
      </v-card>
    </div>

    <!-- Large widget: backups & restore doughnuts with legends -->
    <div class="cloud-backup-widget__charts">
      <coro-widget
        v-for="direction in directions"
        :key="`chart-${direction.id}`"
        class="cloud-backup-widget__chart-card"
        :widget-type="direction.widgetType"
        :loading="false"
        :data-testid="`cloud-backup-${direction.id}-card`"
      >
        <template #header>
          <div class="align-center d-flex w-100 h-100">
            <div class="subtitle1">{{ direction.title }}</div>
          </div>
        </template>
        <template #content>
          <div class="d-flex flex-column align-center pa-6">
            <div class="cloud-backup-widget__doughnut">
              <doughnut-chart
                :data="direction.chartData"
                :options="direction.chartOptions"
              ></doughnut-chart>
            </div>
            <div class="w-100 mt-6" :data-testid="`cloud-backup-${direction.id}-legend`">
              <div
                v-for="item in direction.legend"
                :key="item.status"
                class="d-flex align-center justify-space-between body2 mb-4"
              >
                <div class="d-flex align-center">
                  <span
                    class="cloud-backup-widget__dot mr-3"
                    :style="{ backgroundColor: item.color }"
                  ></span>
                  {{ item.label }}
                </div>
                <div class="subtitle2">{{ item.count }}</div>
              </div>
            </div>
          </div>
        </template>
      </coro-widget>
    </div>
  </div>

  <coro-widget v-else :widget-type="rootWidgetType" :loading="false">
    <template #header>
      <div class="subtitle1">{{ $t("dashboard.cloudBackup.title") }}</div>
    </template>
    <template #content>
      <div class="coro-widget">
        <widget-error-state
          :widget-type="rootWidgetType"
          :title="$t('dashboard.cloudBackup.error')"
          @retry-click="$emit('retry-fetch-data')"
        />
      </div>
    </template>
  </coro-widget>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment/moment";
import { getPercentage } from "@coronet/cui";
import type { DataValue } from "@/types.ts";
import {
  BACKUP_AND_RESTORE_STATUS_COLORS,
  BACKUP_AND_RESTORE_STATUS_ORDER,
  BackupAndRestoreJobStatus,
  type BackupAndRestoreDirectionStats,
  type BackupAndRestoreJobStats,
  type BackupAndRestoreWidgetData,
} from "@/constants/cloud-backup.ts";
import { DoughnutChartColors } from "@/constants/charts.ts";
import DoughnutChart from "@/components/DoughnutChart.vue";
import CoroWidget from "@/components/dashboard/CoroWidget.vue";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";

// Maps each job status to its translation key under dashboard.cloudBackup.statuses.
const STATUS_LABEL_KEYS: Record<BackupAndRestoreJobStatus, string> = {
  [BackupAndRestoreJobStatus.SUCCESSFUL]: "successful",
  [BackupAndRestoreJobStatus.IN_PROGRESS]: "inProgress",
  [BackupAndRestoreJobStatus.SCHEDULED]: "scheduled",
  [BackupAndRestoreJobStatus.INCOMPLETE]: "incomplete",
  [BackupAndRestoreJobStatus.CANCELLED]: "cancelled",
  [BackupAndRestoreJobStatus.UNSUCCESSFUL]: "unsuccessful",
};

const DIRECTION_DEFINITIONS = [
  {
    id: "backups",
    key: "backups",
    widgetType: "cloudBackupBackups",
    titleKey: "dashboard.cloudBackup.backups",
    summaryKey: "dashboard.cloudBackup.totalBackups",
    successKey: "dashboard.cloudBackup.backupsSuccess",
    lastRunTimeKey: "lastBackupTime",
    lastRunLabelKey: "dashboard.cloudBackup.lastBackup",
  },
  {
    id: "restores",
    key: "restores",
    widgetType: "cloudBackupRestore",
    titleKey: "dashboard.cloudBackup.restore",
    summaryKey: "dashboard.cloudBackup.itemsRestored",
    successKey: "dashboard.cloudBackup.restoreSuccess",
    lastRunTimeKey: "lastRestoreTime",
    lastRunLabelKey: "dashboard.cloudBackup.lastRestore",
  },
] as const satisfies ReadonlyArray<{
  id: string;
  key: keyof BackupAndRestoreWidgetData;
  widgetType: string;
  titleKey: string;
  summaryKey: string;
  successKey: string;
  lastRunTimeKey: keyof BackupAndRestoreDirectionStats;
  lastRunLabelKey: string;
}>;

const emptyStats = (): BackupAndRestoreJobStats =>
  BACKUP_AND_RESTORE_STATUS_ORDER.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {} as BackupAndRestoreJobStats);

export default defineComponent({
  components: {
    DoughnutChart,
    CoroWidget,
    WidgetErrorState,
  },
  props: {
    widgetDataValue: {
      type: Object as PropType<DataValue<BackupAndRestoreWidgetData>>,
      required: true,
    },
    // The totals row belongs to the actionboard, where the widget is the only place these
    // numbers appear. The Cloud Backup Overview tab has its own tiles for them, so it
    // switches the row off and shows the doughnuts alone.
    showSummaryWidgets: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const i18n = useI18n();
    const rootWidgetType = "cloudBackup";

    const getTotal = (stats: BackupAndRestoreJobStats): number =>
      BACKUP_AND_RESTORE_STATUS_ORDER.reduce((sum, status) => sum + (stats[status] ?? 0), 0);

    const buildLegend = (stats: BackupAndRestoreJobStats) =>
      BACKUP_AND_RESTORE_STATUS_ORDER.map((status) => ({
        status,
        color: BACKUP_AND_RESTORE_STATUS_COLORS[status],
        label: i18n.t(`dashboard.cloudBackup.statuses.${STATUS_LABEL_KEYS[status]}`),
        count: stats[status] ?? 0,
      }));

    const buildChartData = (legend: ReturnType<typeof buildLegend>) => {
      const colors = legend.map((item) => item.color);
      return {
        labels: legend.map((item) => `${item.label}: ${item.count}`),
        datasets: [
          {
            label: "",
            backgroundColor: colors,
            hoverBackgroundColor: colors,
            data: legend.map((item) => item.count),
          },
        ],
      };
    };

    // With no jobs at all every segment is 0, so Chart.js draws nothing. Render a
    // neutral full ring as a placeholder so the doughnut still shows.
    const buildEmptyChartData = () => {
      const color = DoughnutChartColors.GRAY;
      return {
        labels: [i18n.t("general.noDataAvailable")],
        datasets: [
          {
            label: "",
            backgroundColor: [color],
            hoverBackgroundColor: [color],
            data: [1],
          },
        ],
      };
    };

    const directions = computed(() => {
      return DIRECTION_DEFINITIONS.map((definition) => {
        const stats: BackupAndRestoreDirectionStats =
          props.widgetDataValue.data?.[definition.key] ?? emptyStats();
        const total = getTotal(stats);
        const legend = buildLegend(stats);
        // getPercentage divides by total, so guard the empty case (0/0 -> NaN).
        const successPercentage = total
          ? getPercentage(total, stats[BackupAndRestoreJobStatus.SUCCESSFUL] ?? 0)
          : 0;

        // Absent timestamp -> no "last run" line (leave it empty).
        const lastRunTime = stats[definition.lastRunTimeKey];
        const lastRunLabel = lastRunTime
          ? i18n.t(definition.lastRunLabelKey, { time: moment(lastRunTime).fromNow() })
          : "";

        return {
          id: definition.id,
          widgetType: definition.widgetType,
          title: i18n.t(definition.titleKey),
          summaryLabel: i18n.t(definition.summaryKey),
          lastRunLabel,
          total,
          legend,
          chartData: total ? buildChartData(legend) : buildEmptyChartData(),
          chartOptions: {
            cutout: "85%",
            insideTextData: {
              title: `${successPercentage}%`,
              subtitle: i18n.t(definition.successKey),
              offsetY: 15,
            },
            hoverOffset: 0,
            layout: { padding: 0 },
          },
        };
      });
    });

    return {
      rootWidgetType,
      directions,
    };
  },
});
</script>

<style lang="scss" scoped>
.cloud-backup-widget {
  &__summary,
  &__charts {
    display: flex;
    gap: 16px;
  }

  &__summary-card {
    width: 50%;
  }

  &__chart-card {
    width: 50%;
  }

  &__doughnut {
    width: 210px;
    height: 210px;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }
}

@media only screen and (max-width: 650px) {
  .cloud-backup-widget {
    &__summary,
    &__charts {
      flex-direction: column;
    }

    &__summary-card,
    &__chart-card {
      width: 100%;
    }
  }
}
</style>
